import type { InclusionProofsSource, VerificationReport } from "@sequesign/lib/types";
import {
  FLAG_COPY,
  FLAG_ORDER,
  FLAG_PLAIN_NAME,
  INCLUSION_PROOFS_SOURCE_LABEL,
  INCLUSION_PROOFS_STATUS
} from "../copy.js";

interface FlagTableProps {
  report: VerificationReport;
}

type StatusCell = {
  label: string;
  className: "verified" | "failed" | "not-requested" | "present";
};

function statusFor(value: boolean | null): StatusCell {
  if (value === true) return { label: "Verified", className: "verified" };
  if (value === false) return { label: "Failed verification", className: "failed" };
  return { label: "Not requested", className: "not-requested" };
}

// v0.6 arc: approval/counterparty are tri-state LegState badges, not
// booleans. Render them outside the boolean path so a present badge does
// not show as "Not requested".
function legStateStatusFor(value: string): StatusCell {
  if (value === "present_verified") return { label: "Verified", className: "verified" };
  if (value === "present_unverified")
    return { label: "Present (identity unverified)", className: "present" };
  return { label: "Not present", className: "not-requested" };
}

function inclusionProofsStatusCell(report: VerificationReport): StatusCell {
  const state = report.flags.inclusion_proofs_verified;
  const base = INCLUSION_PROOFS_STATUS[state] ?? INCLUSION_PROOFS_STATUS.not_present;
  // Source is only meaningful when at least one proof was verified.
  // "not_available" maps to an empty suffix; the base label already
  // says "Not requested" or "Failed verification" in that case.
  const source = report.inclusion_proofs?.source as InclusionProofsSource | undefined;
  if (!source) return base;
  const suffix = INCLUSION_PROOFS_SOURCE_LABEL[source];
  if (!suffix) return base;
  return { ...base, label: `${base.label} (${suffix})` };
}

export function FlagTable({ report }: FlagTableProps) {
  return (
    <div className="flag-table" role="table">
      {FLAG_ORDER.map((flag) => {
        const value = report.flags[flag];
        const status: StatusCell =
          flag === "inclusion_proofs_verified"
            ? inclusionProofsStatusCell(report)
            : flag === "approval" || flag === "counterparty"
              ? legStateStatusFor(value as unknown as string)
              : statusFor((value as boolean | null) ?? null);
        return (
          <div className="flag-row" role="row" key={flag}>
            <div className="flag-name" role="cell">
              {FLAG_PLAIN_NAME[flag]}
            </div>
            <div className={`flag-status ${status.className}`} role="cell">
              {status.label}
            </div>
            <div className="flag-explanation" role="cell">
              {FLAG_COPY[flag]}
            </div>
          </div>
        );
      })}
    </div>
  );
}
